import type { IGetActionsData } from "@api";
import DeleteActionButton from "@candidate/components/deleteActionButton";
import { EditActionButton } from "@candidate/components/editActionButton";
import { CheckMarkValidation } from "@complex/Actions/Card/validateButtons/checkMarkValidation";
import type { IModalActionResolver } from "@complex/Actions/Modal/logics/types";
import { Stack } from "@mui/material";
import { Authorizer } from "@navigations/Authorizer";
import clsx from "clsx";
import type { Dispatch, SetStateAction } from "react";
import { useActionCard } from "./logics";
import { ActionCardStack, classes } from "./styles";
import { ActionTitle } from "./title/action";
import { ChallengeTitle } from "./title/challenge";
import { ActionCardTitleDisplay, type IActionPopoverState } from "./types";

interface IActionCardProps {
  setActionPopoverState?: Dispatch<SetStateAction<IActionPopoverState>>;
  updateActionFromList: (actions: IGetActionsData[]) => void;
  removeActionFromList?: (actionsIds: string[]) => void;
  onDelete?: () => void;
  onUpdate?: (formData: IModalActionResolver) => void;
  actions: IGetActionsData[];
  titleDisplay?: ActionCardTitleDisplay;
  disableCandidateField?: boolean;
}

export const ActionCard = ({
  actions,
  setActionPopoverState,
  removeActionFromList,
  onDelete,
  onUpdate,
  updateActionFromList,
  titleDisplay = ActionCardTitleDisplay.ACTION,
  disableCandidateField,
}: IActionCardProps) => {
  const [action, ...groupedActions] = actions;

  const { actionTypeLabel, candidate, statusClass, title } = useActionCard(action);

  const renderActions = () => {
    if (candidate.isArchived) {
      return null;
    }
    return (
      <Stack
        direction="row"
        sx={{
          gap: 1,
        }}
      >
        {(onUpdate || !onDelete) && groupedActions.length < 1 && (
          <Authorizer
            requiredPermissions={["actions.crud"]}
            Authorized={EditActionButton}
            AuthorizedProps={{
              action: action,
              ...(onUpdate
                ? { overrideSubmit: onUpdate }
                : { onCloseModal: (modifiedAction: IGetActionsData) => updateActionFromList([modifiedAction]) }),
              isActionDashboard: true,
              disableCandidateField,
            }}
          />
        )}
        {onDelete ? (
          <DeleteActionButton action={action} mergedActions={groupedActions} onClose={onDelete} />
        ) : (
          removeActionFromList && (
            <Authorizer
              requiredPermissions={["actions.crud"]}
              Authorized={DeleteActionButton}
              AuthorizedProps={{
                action: action,
                mergedActions: groupedActions,
                onClose: (actionsIds: string[]) => removeActionFromList(actionsIds),
              }}
            />
          )
        )}
        {!onDelete && action.status === "todo" && (
          <Authorizer requiredPermissions={["actions.crud"]}>
            <CheckMarkValidation
              actions={actions}
              onChange={(modifiedActions) => updateActionFromList(modifiedActions)}
            />
          </Authorizer>
        )}
      </Stack>
    );
  };
  return (
    <ActionCardStack className={clsx(classes.root, statusClass)}>
      {titleDisplay === ActionCardTitleDisplay.CHALLENGE ? (
        <ChallengeTitle action={action} title={title} setActionPopoverState={setActionPopoverState} />
      ) : (
        <ActionTitle
          action={action}
          groupedActions={groupedActions}
          title={title}
          actionTypeLabel={actionTypeLabel}
          candidate={candidate}
          setActionPopoverState={setActionPopoverState}
          titleDisplay={titleDisplay}
        />
      )}
      {renderActions()}
    </ActionCardStack>
  );
};

export * from "./skeleton";
export * from "./types";
